import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Button,
  Grid,
  Chip,
  Alert,
  AlertTitle,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Card,
  CardContent,
  Tooltip,
  alpha,
  useTheme,
} from '@mui/material';
import {
  AccountBalanceWallet,
  AccountBalance,
  Business,
  Check,
  Settings,
  Refresh,
  Add,
} from '@mui/icons-material';
import toast from 'react-hot-toast';
import { api, bankApi } from '@/services/api';
import { useAppSelector } from '@/hooks/useAppSelector';
import { useAppDispatch } from '@/hooks/useAppDispatch';
import { selectCurrentCompany } from '@/store/slices/authSlice';
import { selectBankAccount, selectAccount, setAccounts } from '@/store/slices/bankAccountSlice';
import type { BankAccountDTO, BankProvider } from '@/types/bank';

const SETTINGS_PATH: Record<BankProvider, string> = {
  sicoob: '/settings/sicoob',
  efi: '/settings/efi',
};

const environmentLabel = (env: string | null) => {
  if (!env) return '—';
  const e = env.toLowerCase();
  if (e === 'production' || e === 'producao') return 'Produção';
  if (e === 'sandbox') return 'Sandbox';
  if (e === 'homologacao') return 'Homologação';
  return env;
};

const formatDocument = (doc?: string) => {
  if (!doc) return '';
  const digits = doc.replace(/\D/g, '');
  if (digits.length === 11) return digits.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  if (digits.length === 14) return digits.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
  return doc;
};

/**
 * Contas bancárias da empresa atual (índice `bank_accounts`).
 * A configuração de cada banco continua na página do provedor; aqui só se lista, seleciona e navega.
 */
const BankAccountsPage: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const company = useAppSelector(selectCurrentCompany);
  const selected = useAppSelector(selectBankAccount);

  const [accounts, setAccountList] = useState<BankAccountDTO[]>([]);
  const [loading, setLoading] = useState(true);
  const [selecting, setSelecting] = useState<string | null>(null);

  const loadAccounts = async () => {
    setLoading(true);
    try {
      const res = await bankApi.accounts();
      if (res?.success) {
        const list = res.data || [];
        setAccountList(list);
        dispatch(setAccounts(list));
      } else {
        toast.error(res?.error?.message || 'Erro ao carregar contas bancárias');
      }
    } catch (error: any) {
      console.error('Error loading bank accounts:', error);
      toast.error(error?.error?.message || 'Erro ao carregar contas bancárias');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setAccountList([]);
    if (company?.id) {
      loadAccounts();
    } else {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company?.id]);

  /** Mesmo fluxo do seletor do header: grava no backend + slice + header da próxima requisição */
  const handleSelect = async (acc: BankAccountDTO) => {
    if (acc.id === selected?.id) return;
    setSelecting(acc.id);
    try {
      const res = await bankApi.select(acc.id);
      const dto = (res?.success && res.data) ? res.data : acc;
      dispatch(setAccounts(accounts));
      dispatch(selectAccount(dto));
      api.setBankAccountId(dto.id);
      toast.success(`Conta selecionada: ${dto.providerLabel} ${dto.accountNumber || dto.label}`);
    } catch (error: any) {
      console.error('Error selecting bank account:', error);
      toast.error(error?.error?.message || 'Erro ao selecionar conta');
    } finally {
      setSelecting(null);
    }
  };

  const configuredCount = accounts.filter(a => a.configured).length;

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h5" fontWeight="bold">
          <AccountBalanceWallet sx={{ mr: 1, verticalAlign: 'middle' }} />
          Contas bancárias
        </Typography>
        <Button variant="outlined" size="small" startIcon={<Refresh />} onClick={loadAccounts}>
          Atualizar
        </Button>
      </Box>

      {/* Banner da Empresa Ativa (mesmo padrão das páginas de integração) */}
      <Alert
        severity="info"
        icon={<Business />}
        sx={{
          mb: 3,
          backgroundColor: alpha(theme.palette.info.main, 0.08),
          border: `1px solid ${alpha(theme.palette.info.main, 0.3)}`,
          '& .MuiAlert-icon': { color: theme.palette.info.main },
        }}
      >
        <AlertTitle sx={{ fontWeight: 600 }}>
          Empresa: {company?.name || 'não identificada'}
        </AlertTitle>
        <Typography variant="body2" color="text.secondary">
          {company?.document ? `Documento: ${formatDocument(company.document)} | ` : ''}
          A conta selecionada é a que o sistema usa em saldo, extrato e Pix. Troque pelo seletor do topo ou pelo botão "Selecionar".
        </Typography>
      </Alert>

      {accounts.length > 0 && configuredCount === 0 && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          <AlertTitle sx={{ fontWeight: 600 }}>Nenhuma conta configurada</AlertTitle>
          As contas abaixo existem mas ainda não têm credenciais/certificado válidos. Clique em "Configurar" para concluir.
        </Alert>
      )}

      {accounts.length === 0 && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          <AlertTitle sx={{ fontWeight: 600 }}>Nenhuma conta bancária</AlertTitle>
          Esta empresa ainda não tem nenhum banco configurado. Configure o Sicoob ou a Efí abaixo — a conta aparece aqui automaticamente depois de configurada.
        </Alert>
      )}

      {/* Tabela de contas */}
      <Paper sx={{ mb: 3 }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Banco</TableCell>
                <TableCell>Conta / Agência</TableCell>
                <TableCell>Ambiente</TableCell>
                <TableCell align="center">Padrão</TableCell>
                <TableCell align="center">Configurada</TableCell>
                <TableCell align="right">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {accounts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                    Nenhuma conta cadastrada para esta empresa
                  </TableCell>
                </TableRow>
              ) : (
                accounts.map(acc => {
                  const isSelected = acc.id === selected?.id;
                  return (
                    <TableRow
                      key={acc.id}
                      hover
                      sx={{
                        backgroundColor: isSelected ? alpha(theme.palette.primary.main, 0.06) : undefined,
                        '&:nth-of-type(even)': { backgroundColor: isSelected ? alpha(theme.palette.primary.main, 0.06) : alpha(theme.palette.action.hover, 0.3) },
                      }}
                    >
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <AccountBalance sx={{ fontSize: 18, color: isSelected ? 'primary.main' : 'text.secondary' }} />
                          <Box>
                            <Typography variant="body2" fontWeight={isSelected ? 600 : 500}>
                              {acc.providerLabel}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {acc.label}
                            </Typography>
                          </Box>
                          {isSelected && (
                            <Chip
                              icon={<Check sx={{ fontSize: 14 }} />}
                              label="Selecionada"
                              size="small"
                              color="primary"
                              variant="outlined"
                              sx={{ height: 20, fontSize: '0.65rem', ml: 1 }}
                            />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{acc.accountNumber || '—'}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {acc.provider === 'sicoob' ? 'Cooperativa' : 'Agência'}: {acc.branch || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{environmentLabel(acc.environment)}</Typography>
                      </TableCell>
                      <TableCell align="center">
                        {acc.isDefault ? <Check sx={{ fontSize: 18, color: 'text.secondary' }} /> : <Typography variant="caption" color="text.disabled">—</Typography>}
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={acc.configured ? 'Sim' : 'Pendente'}
                          size="small"
                          color={acc.configured ? 'success' : 'warning'}
                          variant="outlined"
                          sx={{ height: 20, fontSize: '0.65rem' }}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                          <Tooltip title={isSelected ? 'Conta em uso' : 'Usar esta conta em saldo, extrato e Pix'}>
                            <span>
                              <Button
                                size="small"
                                variant={isSelected ? 'text' : 'contained'}
                                disableElevation
                                disabled={isSelected || selecting !== null}
                                onClick={() => handleSelect(acc)}
                                startIcon={selecting === acc.id ? <CircularProgress size={14} /> : undefined}
                              >
                                {isSelected ? 'Em uso' : 'Selecionar'}
                              </Button>
                            </span>
                          </Tooltip>
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<Settings sx={{ fontSize: 16 }} />}
                            onClick={() => navigate(SETTINGS_PATH[acc.provider])}
                          >
                            Configurar
                          </Button>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Adicionar conta */}
      <Card variant="outlined">
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Add sx={{ color: 'text.secondary' }} />
            <Typography variant="subtitle1" fontWeight={600}>
              Adicionar conta
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Cada banco é configurado na sua própria página (credenciais, certificado e ambiente). Depois de
            configurado, a conta passa a aparecer nesta lista e no seletor "Empresa · Banco" do topo. Hoje é
            permitida uma conta por banco para cada empresa.
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<AccountBalance />}
                onClick={() => navigate('/settings/sicoob')}
                sx={{ justifyContent: 'flex-start', py: 1.25 }}
              >
                Sicoob — Integração Sicoob
              </Button>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<AccountBalance />}
                onClick={() => navigate('/settings/efi')}
                sx={{ justifyContent: 'flex-start', py: 1.25 }}
              >
                Efí — Integração Efí
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Box>
  );
};

export default BankAccountsPage;
